import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import BreadCrumb from "components/common/BreadCrumb";
import TableContainer from "components/common/TableContainer";
import DeleteModal from "components/common/DeleteModal";
import { ColumnDef } from "@tanstack/react-table";
import { IndexColumn } from "components/common/columns";
import { DateTime } from "components/common/Date";
import { getDocumentTitle } from "utils/helpers";
import type { StockLocation } from "features/StockLocations/types/stock-location.types";
import {
  deleteStockLocation,
  getStockLocations,
} from "features/StockLocations/services/StockLocationService";
import { StockLocationModal } from "features/StockLocations/StockLocationForm/StockLocationModal";
import {
  StockLocationType,
  StockLocationTypeLabel,
} from "features/StockLocations/constants/stock-location.constants";
import { toast } from "react-toastify";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import StockLocationListFilters from "./StockLocationListFilters";

const StockLocationList = () => {
  document.title = getDocumentTitle("Stock locations");
  const { can } = useAccess();
  const canCreate = can(Permissions.STOCK_LOCATION.CREATE);
  const canReadLocation = can(Permissions.STOCK_LOCATION.READ);
  const canUpdate = can(Permissions.STOCK_LOCATION.UPDATE);
  const canDelete = can(Permissions.STOCK_LOCATION.DELETE);

  const tableRef = useRef<() => void>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<StockLocation | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<StockLocation | null>(null);
  const [typeFilter, setTypeFilter] = useState<StockLocationType | "">("");

  const requestParams = useMemo(
    () => (typeFilter ? { type: typeFilter } : {}),
    [typeFilter],
  );

  const filtersBadgeCount = useMemo(() => (typeFilter ? 1 : 0), [typeFilter]);

  const clearListFilters = useCallback(() => setTypeFilter(""), []);

  const openCreate = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((row: StockLocation) => {
    setEditing(row);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
  }, []);

  const columns: ColumnDef<StockLocation>[] = useMemo(
    () => [
      IndexColumn<StockLocation>(),
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const sl = row.original;
          return (
            <Link
              to={`/inventory-management/stock-locations/${sl.id}`}
              className="fw-medium link"
            >
              {sl.name}
            </Link>
          );
        },
      },
      {
        header: "Type",
        accessorKey: "type",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const t = row.original.type;
          return t ? (
            <span className="badge bg-primary-subtle text-primary rounded-pill">
              {StockLocationTypeLabel[t]}
            </span>
          ) : (
            "—"
          );
        },
      },
      {
        header: "City",
        accessorKey: "city",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.city?.trim() ? (
            <span className="text-body">{row.original.city}</span>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        header: "State",
        accessorKey: "state",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.state?.trim() ? (
            <span className="text-body">{row.original.state}</span>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.created_at ? (
            <DateTime date={row.original.created_at} />
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        id: "actions",
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const hasMenuItems = canReadLocation || canUpdate || canDelete;
          if (!hasMenuItems) {
            return <span className="text-muted small">—</span>;
          }
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Stock location actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {canReadLocation ? (
                  <DropdownItem
                    tag={Link}
                    to={`/inventory-management/stock-locations/${r.id}`}
                  >
                    <i className="ri-eye-line align-bottom me-2 text-muted" />
                    View
                  </DropdownItem>
                ) : null}
                {canUpdate ? (
                  <DropdownItem onClick={() => openEdit(r)}>
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {canDelete ? (
                  <>
                    {canReadLocation || canUpdate ? (
                      <DropdownItem divider />
                    ) : null}
                    <DropdownItem
                      className="text-danger"
                      onClick={() => {
                        setSelected(r);
                        setDeleteOpen(true);
                      }}
                    >
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [canDelete, canReadLocation, canUpdate, openEdit],
  );

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await deleteStockLocation(selected.id);
      if (res.success) {
        toast.success(res.message);
        tableRef.current?.();
      }
      setDeleteOpen(false);
      setSelected(null);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? err?.message ?? "Delete failed",
      );
    }
  };

  return (
    <React.Fragment>
      <StockLocationModal
        show={modalOpen}
        editing={editing}
        onClose={closeModal}
        onSuccess={() => tableRef.current?.()}
      />
      <DeleteModal
        show={deleteOpen}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setDeleteOpen(false);
          setSelected(null);
        }}
        recordId={selected?.name}
      />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Stock locations"
            pageTitle="Inventory Management"
          />
          <Card>
            <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2 border-bottom">
              <div>
                <h5 className="card-title mb-1">Stock locations</h5>
                <p className="text-muted small mb-0">
                  Warehouses, sites, and vehicles used to store inventory.
                </p>
              </div>
              {canCreate ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={openCreate}
                >
                  <i className="ri-add-line align-bottom me-1" />
                  Add location
                </button>
              ) : null}
            </CardHeader>
            <div className="card-body pt-0">
              <TableContainer
                columns={columns}
                fetchApi={getStockLocations}
                requestParams={requestParams}
                pageSize={10}
                globalSearch
                refreshRef={tableRef}
                divClass="table-responsive table-card mb-0"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light text-muted"
                SearchPlaceholder="Search by name…"
                toolbarRight={<StockLocationListFilters
                  typeFilter={typeFilter}
                  onTypeChange={setTypeFilter}
                />}
                filterCount={filtersBadgeCount}
                onFiltersClear={clearListFilters}
              />
            </div>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default StockLocationList;
